import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, Package, Settings, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Layers, Eye, ListOrdered, Plus, Trash2, X, ArrowLeftRight, Wrench, Star, Pause } from 'lucide-react';
import useBloqueioScroll from '../hooks/useBloqueioScroll';
import { api } from '../lib/api';
import useAuthStore from '../stores/useAuthStore';

const formatDate = (dateObj) => dateObj.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatTimeStr = (dateObj) => dateObj.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const formatDayStr = (dateObj) => dateObj.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit' });
// AAAAMMDD sem separador (padrão Protheus, mesmo formato usado em
// OrdensProducao.jsx) — mandado pro backend como intervalo de consulta
// (GET /api/producao/chao-fabrica?inicio=&fim=).
const aaaammdd = (dateObj) => `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`;
const hhmm = (dateObj) => `${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
// Arredonda pro múltiplo de 15min mais próximo — evita agendamento largado em
// horário "sujo" (ex.: 14:07) ao arrastar um bloco pra nova posição.
const snap15 = (dateObj) => new Date(Math.round(dateObj.getTime() / 900000) * 900000);
// AAAAMMDD <-> "AAAA-MM-DD" (formato que <input type="date"> exige) — sem
// passar por new Date()/toISOString (mesmo cuidado de fuso já documentado no
// resto do sistema).
const aaaammddParaISO = (s) => (s && s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : '');
const isoParaAaaammdd = (s) => (s ? s.replaceAll('-', '') : '');
// Protheus manda AAAAMMDD sem separador — formata só pra exibição (fila/prazo).
const formatPrazo = (aaaammddStr) => {
    if (!aaaammddStr || String(aaaammddStr).length !== 8) return '—';
    const s = String(aaaammddStr);
    return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
};

// Cores sólidas (13/08/2026, repaginação) — mesma linguagem "andon" do resto
// do sistema (Fase 0), e mesmo significado por cor: azul = em execução agora
// (como "EM PRODUÇÃO" em Ordens de Produção), verde = concluído, âmbar =
// pausado, cinza = ciclo encerrado sem status especial. Gradiente trocado
// por cor chapada.
//
// 17/08/2026 (pedido do usuário: Gantt com dado real em vez de mock) — as
// chaves passaram a ser o vocabulário REAL da ZH6 (EM PRODUCAO/PARADO/
// CONCLUIDA/AGUARDANDO), não mais RUNNING/WAITING/DONE/MAINTENANCE/SETUP
// (que eram só do mock — MAINTENANCE e SETUP não têm fonte real hoje, sem
// equivalente na ZH6_STATUS, ficaram de fora). "PARCIAL" é derivado (mesmo
// padrão já usado em OrdensProducao.jsx): ciclo encerrado (AGUARDANDO) mas
// com produção real >0 registrada nele.
// 'SETUP_ANDAMENTO' (25/08/2026, pedido do usuário) — setup REAL acontecendo
// agora (ZS1), diferente do "Setup" que já existe em PLANEJADO_COLORS (esse
// é só o agendamento manual/ZP1 do PCP, ainda não aconteceu — tracejado).
// Mesmo roxo dos dois, de propósito (é o mesmo conceito pro usuário), mas
// esse aqui entra em STATUS_COLORS — mesmo tratamento sólido dos blocos reais
// (statusVisualDoBloco), não o tracejado do planejado.
const STATUS_COLORS = {
    'EM PRODUCAO': { bg: 'bg-brand-500',      text: 'text-white', label: 'Em Execução' },
    'PARADO':      { bg: 'bg-amber-500',      text: 'text-white', label: 'Pausada' },
    'CONCLUIDA':   { bg: 'bg-petrol-500',     text: 'text-white', label: 'Concluído' },
    'PARCIAL':     { bg: 'bg-amber-600',      text: 'text-white', label: 'Produzido Parcial' },
    'AGUARDANDO':  { bg: 'bg-anthracite-600', text: 'text-white', label: 'Encerrado' },
    'SETUP_ANDAMENTO': { bg: 'bg-roxo-500',   text: 'text-white', label: 'Setup em andamento' },
    // Setup FINALIZADO, histórico (26/08/2026, pedido do usuário: "quando eu
    // encerrei o setup... sumiu do gráfico, eles devem ficar registrados") —
    // antes só existia o "ao vivo" (SETUP_ANDAMENTO); assim que o setup era
    // liberado, o bloco desaparecia do Gantt sem deixar rastro.
    //
    // Cor mais escura pra "concluído" REVERTIDA (27/08/2026, pedido do
    // usuário: "não queria que elas mudassem de cor quando concluídas") —
    // mesmo roxo/vermelho de ANDAMENTO agora, sempre; `concluido: true` liga
    // uma bolinha verde no canto do bloco (renderOpBlock) pra sinalizar
    // "já aconteceu", sem precisar de uma cor nova por categoria.
    'SETUP_CONCLUIDO': { bg: 'bg-roxo-500',   text: 'text-white', label: 'Setup concluído', concluido: true },
    // Repreparação (27/08/2026, pedido do usuário) — mesmo conceito de
    // SETUP_ANDAMENTO/SETUP_CONCLUIDO acima, só que pro 2º tipo de setup
    // (refazer depois, não a 1ª preparação) — cor indigo, mesma família
    // "roxa" do Setup mas distinguível (mesmo indigo usado em Producao.jsx/
    // OrdensProducao.jsx pra manter a paleta consistente entre as telas).
    'REPREPARACAO_ANDAMENTO': { bg: 'bg-indigo-500', text: 'text-white', label: 'Repreparação em andamento' },
    'REPREPARACAO_CONCLUIDO': { bg: 'bg-indigo-500', text: 'text-white', label: 'Repreparação concluída', concluido: true },
    // Manutenção "ao vivo"/histórica (26/08/2026, pedido do usuário) — mesmo
    // vermelho já usado pra Manutenção em PLANEJADO_COLORS/Manutencao.jsx,
    // só que sólido (mesmo tratamento de SETUP_ANDAMENTO acima) por ser algo
    // que já aconteceu/está acontecendo, não só um plano.
    'MANUTENCAO_ANDAMENTO': { bg: 'bg-red-600', text: 'text-white', label: 'Manutenção em andamento' },
    'MANUTENCAO_CONCLUIDA': { bg: 'bg-red-600', text: 'text-white', label: 'Manutenção concluída', concluido: true },
    // Horas improdutivas (31/08/2026, chamado MATA682) — parada de máquina
    // lançada hoje só pela rotina nativa do Protheus (treinamento, espera,
    // retrabalho fora do nosso apontamento, etc.), sem OP associada. Cor
    // slate: neutra, não compete com nenhuma cor já usada (nem o vermelho de
    // Manutenção, que é especificamente "máquina com problema") — confirmado
    // com o usuário.
    'IMPRODUTIVO_ANDAMENTO': { bg: 'bg-slate-500', text: 'text-white', label: 'Improdutivo em andamento' },
    'IMPRODUTIVO_CONCLUIDO': { bg: 'bg-slate-500', text: 'text-white', label: 'Improdutivo', concluido: true },
};
// Bloco real (ZH6) não tem status "Aguardando nunca tocado" — é sempre um
// ciclo que JÁ aconteceu. AGUARDANDO aqui significa "ciclo fechado, sem
// status especial" (reset padrão da ZH6 entre ciclos), daí a distinção com
// PARCIAL quando teve produção de verdade.
const statusVisualDoBloco = (bloco) => {
    if (bloco.status === 'AGUARDANDO' && (bloco.produzido || 0) > 0) return 'PARCIAL';
    return STATUS_COLORS[bloco.status] ? bloco.status : 'AGUARDANDO';
};

// Blocos "planejado" (17/08/2026, agendamento real — tabela ZP1) são um plano
// colocado na mão pelo PCP, ainda não aconteceu — tratamento visual distinto
// dos blocos reais (ZH6) de propósito: borda tracejada (nunca backdrop-filter,
// dentro da mesma restrição de RDP do resto do sistema), pra ficar claro à
// primeira vista "isso é plano, não aconteceu ainda". Mesmo vocabulário de
// cor já usado em Maquinarios.jsx pro status Setup/Manutenção.
//
// 18/08/2026 (bug real, usuário reportou tema claro ilegível): a 1ª versão
// usava fundo bem translúcido (`/20`) + texto claro (`-200`), pensado só pra
// fundo escuro — no tema claro isso virava texto claro sobre fundo quase
// branco, quase invisível. Corrigido pro MESMO padrão já comprovado do resto
// do sistema (STATUS_COLORS): `solid` (fundo saturado) + branco sempre —
// contraste garantido em qualquer tema, porque não depende da cor da página
// por trás. `tint`/`icon` (mais suaves) só pra ícone dentro de um badge
// pequeno, mesmo tratamento já usado no modal do bloco real (`bg-brand-500/10`
// + `text-brand-400`) — também seguro nos dois temas, já testado no resto do
// sistema.
const PLANEJADO_COLORS = {
    PRODUCAO:    { solid: 'bg-brand-500',  border: 'border-brand-500',  tint: 'bg-brand-500/10',  icon: 'text-brand-400',  label: 'Produção (planejada)' },
    SETUP:       { solid: 'bg-roxo-500', border: 'border-roxo-500', tint: 'bg-roxo-500/10', icon: 'text-roxo-500', label: 'Setup' },
    MANUTENCAO:  { solid: 'bg-red-500',    border: 'border-red-500',   tint: 'bg-red-500/10',    icon: 'text-red-500',    label: 'Manutenção' },
};

// Zoom multinível (18/08/2026, pedido do usuário, pesquisado contra padrões
// reais de Gantt/calendário — GanttPRO/Inforiver fazem transição Dia → Hora →
// Minuto por nível de zoom com intervalo configurável; Google Calendar (mobile)
// faz o mesmo com pinça, indo até blocos de 15min). Cada nível define 2 coisas
// juntas: a largura em px de 1 hora (afeta só o espaçamento/tamanho dos blocos,
// já existia) e a granularidade das colunas no modo Diário, em minutos (nova —
// antes a coluna era sempre fixa em 24 blocos de 1h, zoom só esticava o mesmo
// grid, nunca refinava). O nível 3 (120px/h, colunas de 1h) é o padrão de
// sempre — só ficou no MEIO de um intervalo maior agora, pra dar espaço tanto
// pra afastar quanto pra aproximar até minutos, sem mudar a aparência inicial.
const ZOOM_LEVELS = [
    { pxPorHora: 40, minutosPorColuna: 60 },
    { pxPorHora: 70, minutosPorColuna: 60 },
    { pxPorHora: 120, minutosPorColuna: 60 },
    { pxPorHora: 180, minutosPorColuna: 30 },
    { pxPorHora: 280, minutosPorColuna: 15 },
    { pxPorHora: 450, minutosPorColuna: 10 },
    { pxPorHora: 700, minutosPorColuna: 5 },
];

const ChaoFabrica = () => {
    // Reatribuição de máquina (25/08/2026, pedido do usuário) — só quem tem
    // essa permissão (PCP/supervisão) vê o botão "Reatribuir" na fila; mesmo
    // padrão do "preparador" em Producao.jsx (temPermissao sempre true pra
    // admin).
    const { temPermissao } = useAuthStore();
    const podeReatribuir = temPermissao('reatribuir_maquina');
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [viewMode, setViewMode] = useState('Diario');
    const [zoomIndex, setZoomIndex] = useState(2);
    const [detailLevel, setDetailLevel] = useState('detalhado');
    const [baseDate, setBaseDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });

    // Dado real (17/08/2026, pedido do usuário: Gantt funcional, não mock) —
    // GET /api/producao/chao-fabrica já devolve agrupado por Centro de
    // Trabalho, cada máquina já com seus blocos (ZH6, com hora real) e fila
    // (OPs pendentes, sem hora — ver backend/src/controllers/producao.js).
    const [grupos, setGrupos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erroApi, setErroApi] = useState('');
    const [selectedOp, setSelectedOp] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Agendamento manual (17/08/2026, tabela ZP1) — quadro do PCP, não motor de
    // nivelamento automático (ver plano). `novoBloco` abre o formulário de
    // criação (Setup/Manutenção direto, ou Produção pré-preenchido a partir de
    // um item da fila); `formErro`/`salvando` são compartilhados pelos 2
    // formulários (criação e edição de um bloco planejado existente).
    const [novoBloco, setNovoBloco] = useState(null);
    const [formErro, setFormErro] = useState('');
    const [salvando, setSalvando] = useState(false);
    const draggingRef = useRef(null);
    // Painel "Fila de Espera" (18/08/2026, pedido do usuário) — o tooltip por
    // hover (abaixo) só mostra a fila de UMA máquina por vez, sem dar pro
    // supervisor a visão de conjunto pra decidir onde agendar o quê. Este
    // painel lateral mostra a fila de TODAS as máquinas de uma vez, mesmo
    // dado (`grupos[].maquinas[].fila`), mesma ação "Agendar" por item.
    const [filaPanelAberto, setFilaPanelAberto] = useState(false);
    // Modal "OPs da máquina" (18/08/2026, pedido do usuário) — clicar na parte
    // vazia da linha da máquina (não no botão "+" nem no badge "N na fila",
    // que têm sua própria ação/tooltip — por isso os 2 têm stopPropagation)
    // abre um modal só com a fila DESSA máquina, mesmo padrão de item já usado
    // no painel "Fila de Espera" (grade de 4 colunas), só que sem precisar
    // abrir o painel geral pra ver 1 máquina só.
    const [maquinaOpsModal, setMaquinaOpsModal] = useState(null);
    // Reatribuição de máquina (25/08/2026) — abre a partir de um item da fila
    // (painel "Fila de Espera" ou modal "OPs da máquina"), guarda a máquina
    // ORIGINAL (de onde o item veio) + a OP/operação sendo reatribuída.
    // `recursoNovo`/`motivo` são o formulário do próprio modal.
    const [reatribuirModal, setReatribuirModal] = useState(null);
    const [reatribuirErro, setReatribuirErro] = useState('');
    const [reatribuindo, setReatribuindo] = useState(false);
    useBloqueioScroll(isModalOpen || !!novoBloco || filaPanelAberto || !!maquinaOpsModal || !!reatribuirModal);

    // Grupos de máquina colapsáveis (13/08/2026) — importante pra continuar
    // navegável quando o centro de trabalho real tem muito mais recursos que
    // os 5 do mock original.
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const toggleGroup = (type) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
        });
    };

    const containerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const currentTimeRef = useRef(null);
    const pendingScrollRatio = useRef(null);

    const toggleFullScreen = () => {
        if (isFullScreen && !document.fullscreenElement) {
            setIsFullScreen(false);
            return;
        }
        try {
            if (!document.fullscreenElement && containerRef.current) {
                const promise = containerRef.current.requestFullscreen();
                if (promise && promise.catch) {
                    promise.catch(err => {
                        console.warn("Fullscreen nativo bloqueado por política de permissão. Ativando modo expandido.", err);
                        setIsFullScreen(true);
                    });
                } else {
                    setIsFullScreen(true);
                }
            } else if (document.fullscreenElement) {
                const promise = document.exitFullscreen();
                if (promise && promise.catch) {
                    promise.catch(err => console.error(err));
                }
            }
        } catch (err) {
            console.warn("Erro ao usar Fullscreen API. Ativando modo expandido seguro.", err);
            setIsFullScreen(!isFullScreen);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (document.fullscreenElement) {
                setIsFullScreen(true);
            } else if (!document.fullscreenElement && isFullScreen) {
                setIsFullScreen(false);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [isFullScreen]);

    const changeViewMode = (vm) => {
        setViewMode(vm);
        if (vm === 'Diario') setZoomIndex(6);
        if (vm === 'Semanal') setZoomIndex(4);
        if (vm === 'Quinzenal') setZoomIndex(3);
        if (vm === 'Mensal') setZoomIndex(2);
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        setBaseDate(d);
        setTimeout(scrollToCurrentTime, 150);
    };

    const handleZoom = (direction) => {
        const container = scrollContainerRef.current;
        if (container) {
            pendingScrollRatio.current = (container.scrollLeft + container.clientWidth / 2) / container.scrollWidth;
        }
        if (direction === 'in') setZoomIndex(prev => Math.min(ZOOM_LEVELS.length - 1, prev + 1));
        if (direction === 'out') setZoomIndex(prev => Math.max(0, prev - 1));
    };

    const handleDateNav = (dir) => {
        const d = new Date(baseDate);
        const amt = dir === 'next' ? 1 : -1;
        if (viewMode === 'Diario') d.setDate(d.getDate() + (1 * amt));
        if (viewMode === 'Semanal') d.setDate(d.getDate() + (7 * amt));
        if (viewMode === 'Quinzenal') d.setDate(d.getDate() + (14 * amt));
        if (viewMode === 'Mensal') d.setMonth(d.getMonth() + (1 * amt));
        d.setHours(0, 0, 0, 0);
        setBaseDate(d);
    };

    const handleToday = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        setBaseDate(d);
        setTimeout(scrollToCurrentTime, 150);
    };

    const scrollToCurrentTime = () => {
        if (currentTimeRef.current && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const line = currentTimeRef.current;
            // 320 = largura da coluna Setor/Máquina (w-80, alargada 18/08/2026
            // pra caber nome completo de máquina/centro de trabalho sem cortar,
            // e de novo 19/08/2026 pra caber o código antes do nome).
            const scrollLeft = line.offsetLeft - (container.clientWidth / 2) + 320;
            container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (pendingScrollRatio.current !== null && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const targetScroll = (pendingScrollRatio.current * container.scrollWidth) - (container.clientWidth / 2);
            container.scrollTo({ left: Math.max(0, targetScroll), behavior: 'auto' });
            pendingScrollRatio.current = null;
        }
    }, [zoomIndex]);

    useEffect(() => {
        const timeout = setTimeout(scrollToCurrentTime, 150);
        return () => clearTimeout(timeout);
    }, [baseDate]);

    const getTimelineMetrics = () => {
        const startD = new Date(baseDate);
        if (viewMode === 'Mensal') startD.setDate(1);
        const endD = new Date(startD);
        if (viewMode === 'Diario') endD.setDate(endD.getDate() + 1);
        if (viewMode === 'Semanal') endD.setDate(endD.getDate() + 7);
        if (viewMode === 'Quinzenal') endD.setDate(endD.getDate() + 14);
        if (viewMode === 'Mensal') endD.setDate(endD.getDate() + new Date(startD.getFullYear(), startD.getMonth() + 1, 0).getDate());
        return { startMs: startD.getTime(), endMs: endD.getTime(), totalMs: endD.getTime() - startD.getTime(), startDate: startD, endDate: endD };
    };
    const timelineMetrics = getTimelineMetrics();
    const nowMs = new Date().getTime();

    // Busca dado real sempre que o intervalo visível muda (navegação de data
    // ou troca de Dia/Semana/Quinzenal/Mês) — mesmo intervalo já calculado
    // acima pro cabeçalho de colunas, só formatado AAAAMMDD pro backend.
    const carregarChaoFabrica = (cancelObj) => {
        setCarregando(true);
        const inicio = aaaammdd(timelineMetrics.startDate);
        const fim = aaaammdd(new Date(timelineMetrics.endMs - 1000));
        return api.get(`/api/producao/chao-fabrica?inicio=${inicio}&fim=${fim}`)
            .then(({ grupos: gruposResp }) => { if (!cancelObj?.cancelado) { setGrupos(gruposResp || []); setErroApi(''); } })
            .catch((err) => { if (!cancelObj?.cancelado) setErroApi(err.message || 'Erro ao carregar dados do Chão de Fábrica.'); })
            .finally(() => { if (!cancelObj?.cancelado) setCarregando(false); });
    };

    useEffect(() => {
        const cancelObj = { cancelado: false };
        carregarChaoFabrica(cancelObj);
        return () => { cancelObj.cancelado = true; };
    }, [timelineMetrics.startMs, timelineMetrics.endMs]);

    // Confirma a reatribuição (25/08/2026) — POST /api/reatribuicoes, depois
    // recarrega o Chão de Fábrica inteiro: a fila da máquina original perde o
    // item, a da máquina nova ganha, tudo pela mesma leitura já corrigida em
    // producao.js (recursoEfetivo), sem precisar de lógica otimista aqui.
    const handleConfirmarReatribuicao = async () => {
        if (!reatribuirModal?.recursoNovo) { setReatribuirErro('Escolha a máquina nova.'); return; }
        setReatribuindo(true);
        setReatribuirErro('');
        try {
            await api.post('/api/reatribuicoes', {
                op: reatribuirModal.op,
                operacao: reatribuirModal.operacao,
                recurso_original: reatribuirModal.maquina,
                recurso_novo: reatribuirModal.recursoNovo,
                motivo: reatribuirModal.motivo || '',
            });
            setReatribuirModal(null);
            carregarChaoFabrica();
        } catch (err) {
            setReatribuirErro(err.message || 'Erro ao reatribuir máquina.');
        } finally {
            setReatribuindo(false);
        }
    };

    // Volta a operação pra máquina do roteiro (25/08/2026) — usado pelo botão
    // "Cancelar" que aparece no lugar de "Reatribuir" quando o item já está
    // reatribuído (ver f.reatribuida_de).
    const handleCancelarReatribuicao = async (op, operacao) => {
        try {
            await api.post('/api/reatribuicoes/cancelar', { op, operacao });
            carregarChaoFabrica();
        } catch (err) {
            setErroApi(err.message || 'Erro ao cancelar reatribuição.');
        }
    };

    const generateColumns = () => {
        const cols = [];
        const pointer = new Date(timelineMetrics.startDate);
        if (viewMode === 'Diario') {
            // Granularidade das colunas segue o zoom (18/08/2026, pedido do
            // usuário) — antes era sempre 24 colunas fixas de 1h; zoom só
            // esticava o mesmo grid sem nunca refinar. Agora, a partir de
            // certo nível de zoom, a própria coluna passa a ser de 30/15/10/5
            // minutos (ver ZOOM_LEVELS acima).
            const passoMin = ZOOM_LEVELS[zoomIndex].minutosPorColuna;
            for (let m = 0; m < 24 * 60; m += passoMin) {
                cols.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
            }
        } else {
            const diffDays = Math.round(timelineMetrics.totalMs / (1000 * 60 * 60 * 24));
            for (let i = 0; i < diffDays; i++) {
                const d = new Date(pointer);
                d.setDate(pointer.getDate() + i);
                const dayStr = d.toLocaleDateString('pt-BR', { weekday: 'short' });
                cols.push(`${dayStr.charAt(0).toUpperCase() + dayStr.slice(1, 3)}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`);
            }
        }
        return cols;
    };
    const columns = generateColumns();

    // Período por extenso (18/09/2026, pedido do usuário: "as datas estão
    // ruins de ler") — antes era "18/09 - 2026" em 11px, cinza e em
    // maiúsculas. Agora: "Sexta, 18 de setembro de 2026" no Diário,
    // "14 a 20 de setembro de 2026" na semana/quinzena (com o mês nos dois
    // lados quando o período vira o mês) e "Setembro de 2026" no Mensal.
    const formatHeaderDate = () => {
        const inicio = timelineMetrics.startDate;
        const fim = new Date(timelineMetrics.endMs - 1000);
        const mes = (d) => d.toLocaleString('pt-BR', { month: 'long' });
        if (viewMode === 'Mensal') {
            const rotulo = `${mes(inicio)} de ${inicio.getFullYear()}`;
            return rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
        }
        if (viewMode === 'Diario') {
            const semana = inicio.toLocaleString('pt-BR', { weekday: 'long' }).split('-')[0];
            return `${semana.charAt(0).toUpperCase() + semana.slice(1)}, ${inicio.getDate()} de ${mes(inicio)} de ${inicio.getFullYear()}`;
        }
        if (inicio.getFullYear() !== fim.getFullYear()) {
            return `${inicio.getDate()} de ${mes(inicio)} de ${inicio.getFullYear()} a ${fim.getDate()} de ${mes(fim)} de ${fim.getFullYear()}`;
        }
        if (inicio.getMonth() !== fim.getMonth()) {
            return `${inicio.getDate()} de ${mes(inicio)} a ${fim.getDate()} de ${mes(fim)} de ${fim.getFullYear()}`;
        }
        return `${inicio.getDate()} a ${fim.getDate()} de ${mes(fim)} de ${fim.getFullYear()}`;
    };

    // Rótulo que a coluna de hoje teria nos modos por dia ("Sex, 18/09"),
    // pra destacar essa coluna no cabeçalho.
    const hojeRotuloColuna = (() => {
        const d = new Date(nowMs);
        const dia = d.toLocaleDateString('pt-BR', { weekday: 'short' });
        return `${dia.charAt(0).toUpperCase() + dia.slice(1, 3)}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();

    // Agrupamento por Centro de Trabalho real (17/08/2026, confirmado com o
    // usuário) — já vem pronto do backend (SHB, mesmo critério já usado em
    // Maquinário e no seletor de máquina do login), não precisa mais reduzir
    // uma lista de máquinas por um campo "tipo".
    const ROW_HEIGHT = detailLevel === 'detalhado' ? 90 : 45;
    const HEADER_HEIGHT = 32;

    const layout = [];
    let currentY = 0;
    const maquinaYMap = {};
    // Todos os blocos de todas as máquinas visíveis, num array só — usado
    // pra desenhar as linhas de dependência entre operações da mesma OP
    // (ver renderGanttTimeline). Guardado aqui porque só existe depois que
    // o layout (com maquinaYMap) foi montado.
    const todosBlocos = [];

    grupos.forEach((grupo) => {
        const isCollapsed = collapsedGroups.has(grupo.centroTrabalho);
        layout.push({ isHeader: true, id: `header-${grupo.centroTrabalho}`, title: grupo.centroTrabalho, height: HEADER_HEIGHT, top: currentY, count: grupo.maquinas.length, collapsed: isCollapsed });
        currentY += HEADER_HEIGHT;
        if (!isCollapsed) {
            grupo.maquinas.forEach((m) => {
                // "Ocupação no período" (% do intervalo visível coberto por bloco
                // real) removida 18/08/2026 a pedido do usuário — na prática dava
                // quase sempre 0.0% (só conta bloco JÁ acontecido dentro da janela
                // visível, não a fila/planejado), virando ruído visual sem
                // informação útil na maioria das visualizações.
                (m.blocos || []).forEach((bloco) => {
                    todosBlocos.push({ ...bloco, maquinaCodigo: m.codigo });
                });
                maquinaYMap[m.codigo] = currentY + (ROW_HEIGHT / 2);
                layout.push({ isHeader: false, id: `maq-${m.codigo}`, data: m, height: ROW_HEIGHT, top: currentY });
                currentY += ROW_HEIGHT;
            });
        }
    });

    // Bloco real (ZH6, com hora) — 17/08/2026, pedido do usuário: Gantt com
    // dado real em vez de mock. Sem arrastar-e-soltar (não existe onde
    // persistir uma reprogramação de verdade hoje — ver plano) e sem barra de
    // progresso interna (um bloco real já é algo que ACONTECEU, não tem "%
    // completo" no mesmo sentido que uma OP agendada tinha no mock).
    const renderOpBlock = (bloco) => {
        const opStartMs = new Date(bloco.inicio).getTime();
        const opEndMs = new Date(bloco.fim || bloco.inicio).getTime();
        if (opEndMs <= timelineMetrics.startMs || opStartMs >= timelineMetrics.endMs) return null;

        let leftPerc = ((opStartMs - timelineMetrics.startMs) / timelineMetrics.totalMs) * 100;
        let widthPerc = ((opEndMs - Math.max(opStartMs, timelineMetrics.startMs)) / timelineMetrics.totalMs) * 100;
        let isCutLeft = false, isCutRight = false;
        // Bug real corrigido (27/08/2026, usuário reportou: sessão "ao vivo"
        // aberta desde o dia anterior aparecia como uma tira minúscula em vez
        // de ir até "agora") — `widthPerc` acima já usa Math.max(opStartMs,
        // timelineMetrics.startMs), ou seja, já vem CORRETAMENTE recortado
        // pra não contar a parte fora da janela visível à esquerda. Somar
        // `leftPerc` (negativo, quando o bloco começa antes da janela) aqui
        // de novo descontava essa mesma parte DUAS vezes, encolhendo a
        // largura por engano sempre que um bloco cruzava a meia-noite.
        if (leftPerc < 0) { isCutLeft = true; leftPerc = 0; }
        if (leftPerc + widthPerc > 100) { isCutRight = true; widthPerc = 100 - leftPerc; }

        const statusVisual = statusVisualDoBloco(bloco);
        const theme = STATUS_COLORS[statusVisual];
        // Bloco "ao vivo" (25/08/2026) — ver mesmo comentário no modal de
        // detalhe abaixo: o "fim" que vem da API pra EM PRODUCAO/
        // SETUP_ANDAMENTO é só a hora da consulta, não um término de verdade.
        const emAndamento = bloco.status === 'EM PRODUCAO' || bloco.status === 'SETUP_ANDAMENTO' || bloco.status === 'MANUTENCAO_ANDAMENTO' || bloco.status === 'IMPRODUTIVO_ANDAMENTO';
        // Manutenção (26/08/2026) — mesmo bloco "ao vivo"/histórico dos
        // outros, mas sem OP/operação/produto/produzido (não faz sentido pra
        // uma parada de máquina) — título/tooltip usam Tipo/Descrição/
        // Solicitante da OS em vez disso.
        const ehManutencao = bloco.origem === 'MANUTENCAO';
        // Horas improdutivas (31/08/2026, chamado MATA682) — mesma lógica de
        // "sem OP" da Manutenção acima, só que vindo direto da SH6 nativa
        // (H6_TIPO='I'), com descrição em texto livre (H6_OBSERVA) no lugar
        // de tipo/solicitante de uma OS.
        const ehImprodutivo = bloco.origem === 'IMPRODUTIVO';
        // Projeto Especial (29/08/2026, pedido do usuário) — destaque amarelo
        // pra diferenciar de OPs normais, POR CIMA da cor de status (borda,
        // não troca o fundo — mesmo raciocínio já usado pra "concluído": não
        // perder a informação de status só pra sinalizar uma categoria a
        // mais). Vem de SC2.C2_ZESPECI via tipo_especial no bloco.
        const ehEspecial = bloco.tipo_especial === '2';

        const blockHeight = detailLevel === 'detalhado' ? 'calc(100% - 14px)' : 'calc(100% - 8px)';
        const blockTop = detailLevel === 'detalhado' ? '7px' : '4px';

        return (
            <div
                key={ehManutencao ? `mnt-${bloco.osId}` : ehImprodutivo ? `imp-${bloco.inicio}` : `${bloco.op}-${bloco.operacao}-${bloco.inicio}`}
                onClick={() => { setSelectedOp(bloco); setIsModalOpen(true); }}
                className={`gantt-op-block ${theme.bg} text-white flex flex-col px-3 group shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer ${ehEspecial ? 'border-2 border-yellow-400' : ''}`}
                style={{
                    top: blockTop,
                    height: blockHeight,
                    left: `${leftPerc}%`,
                    width: `calc(${widthPerc}%)`,
                    borderTopLeftRadius: isCutLeft ? '0' : '12px',
                    borderBottomLeftRadius: isCutLeft ? '0' : '12px',
                    borderTopRightRadius: isCutRight ? '0' : '12px',
                    borderBottomRightRadius: isCutRight ? '0' : '12px'
                }}
            >
                {/* Bolinha "concluído" (27/08/2026, pedido do usuário) — sinaliza
                    que já terminou sem precisar de uma cor própria por
                    categoria (ver `concluido` em STATUS_COLORS). Canto fixo,
                    funciona em qualquer largura de bloco — um risco vertical
                    tomaria o bloco inteiro num bloco bem estreito. */}
                {theme.concluido && (
                    <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-petrol-400 ring-1 ring-black/20 pointer-events-none" title="Concluído" />
                )}
                {ehEspecial && (
                    <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-yellow-400 flex items-center justify-center pointer-events-none" title="Projeto Especial">
                        <Star size={9} className="text-anthracite-900" fill="currentColor" />
                    </div>
                )}
                {detailLevel === 'resumido' ? (
                    <div className="h-full flex flex-col justify-center">
                        <div className="font-bold text-[10px] truncate pointer-events-none leading-none tracking-wide">{ehManutencao ? bloco.tipoOS : ehImprodutivo ? bloco.descricao : bloco.op}</div>
                    </div>
                ) : (
                    <div className="flex-1 py-0.5 flex flex-col justify-between">
                        <div>
                            <div className="font-bold text-[11px] truncate pointer-events-none leading-tight tracking-wide">
                                {ehManutencao ? <><Wrench size={11} className="inline -mt-0.5 mr-1" />{bloco.tipoOS}</> : ehImprodutivo ? <><Pause size={11} className="inline -mt-0.5 mr-1" />Improdutivo</> : <>{bloco.op} <span className="opacity-70 font-medium">· Op.{bloco.operacao}</span></>}
                            </div>
                            {ZOOM_LEVELS[zoomIndex].pxPorHora >= 70 && (
                                <div className="text-[10px] opacity-80 truncate pointer-events-none leading-tight flex items-center gap-1 mt-0.5">
                                    {ehManutencao ? bloco.descricao : ehImprodutivo ? bloco.descricao : (<><Package size={9} /> {bloco.produto_nome || bloco.produto}</>)}
                                </div>
                            )}
                        </div>

                        {ZOOM_LEVELS[zoomIndex].pxPorHora >= 120 && (
                            <div className="flex flex-col gap-[2px] mb-0.5 text-[9px] font-medium bg-white/10 rounded-xl px-2.5 py-1.5 pointer-events-none">
                                <div className="flex justify-between">
                                    <span className="opacity-60">Início</span> <span>{formatTimeStr(new Date(bloco.inicio))}</span>
                                </div>
                                <div className="flex justify-between text-yellow-100">
                                    <span className="opacity-60">Fim</span> <span>{emAndamento ? 'Em andamento' : (bloco.fim ? formatTimeStr(new Date(bloco.fim)) : '—')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Hover Tooltip */}
                {/* Cores literais (text-[#hex]), NÃO os tokens semânticos
                    (text-anthracite-300/text-white/etc) — 18/08/2026, achado
                    real: este tooltip é SEMPRE escuro, mas os tokens
                    semânticos são remapeados pro tema claro (index.css,
                    "html:not(.dark) .text-anthracite-300 { color: #424245 }"
                    — pensado pra superfícies que TROCAM de cor com o tema).
                    Num box que nunca muda de cor, isso virava texto escuro
                    sobre fundo escuro, quase ilegível, no tema claro. */}
                <div className="absolute hidden group-hover:block z-50 bg-[#0f1219] text-[#ffffff] p-4 rounded-2xl shadow-float-lg bottom-full mb-3 left-1/2 -translate-x-1/2 w-72 text-xs font-normal border border-[rgba(255,255,255,0.08)] whitespace-nowrap pointer-events-none">
                    <div className="flex justify-between border-b border-[rgba(255,255,255,0.06)] pb-2.5 mb-2.5">
                        <span className="font-bold text-sm text-[#cf6428]">{ehManutencao ? bloco.tipoOS : ehImprodutivo ? 'Improdutivo' : <>{bloco.op} · Operação {bloco.operacao}</>}</span>
                        <div className="flex items-center gap-1.5">
                            {ehEspecial && (
                                <span className="flex items-center gap-1 text-[10px] bg-yellow-400 text-anthracite-900 px-2.5 py-1 rounded-xl font-bold">
                                    <Star size={10} fill="currentColor" /> Projeto Especial
                                </span>
                            )}
                            <span className="text-[10px] bg-[rgba(255,255,255,0.06)] px-2.5 py-1 rounded-xl font-medium">{theme.label}</span>
                        </div>
                    </div>
                    {ehManutencao ? (
                        <>
                            <p className="mb-1.5 whitespace-normal text-[#9da0ab]">{bloco.descricao}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Início:</span> {formatDate(new Date(bloco.inicio))}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Fim:</span> {emAndamento ? 'Em andamento' : (bloco.fim ? formatDate(new Date(bloco.fim)) : '—')}</p>
                            <p className="text-[#9da0ab]"><span className="text-[#5e606f]">Solicitante:</span> {bloco.solicitante || '—'}</p>
                        </>
                    ) : ehImprodutivo ? (
                        <>
                            <p className="mb-1.5 whitespace-normal text-[#9da0ab]">{bloco.descricao}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Início:</span> {formatDate(new Date(bloco.inicio))}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Fim:</span> {emAndamento ? 'Em andamento' : (bloco.fim ? formatDate(new Date(bloco.fim)) : '—')}</p>
                            <p className="text-[#9da0ab]"><span className="text-[#5e606f]">Registrado por:</span> {bloco.operador || '—'}</p>
                        </>
                    ) : (
                        <>
                            <p className="mb-1.5 flex items-center gap-2 text-[#9da0ab]"><Package size={12} /> {bloco.produto_nome || bloco.produto}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Início:</span> {formatDate(new Date(bloco.inicio))}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Fim:</span> {emAndamento ? 'Em andamento' : (bloco.fim ? formatDate(new Date(bloco.fim)) : '—')}</p>
                            <p className="mb-1.5 text-[#9da0ab]"><span className="text-[#5e606f]">Produzido:</span> {bloco.produzido ?? 0} <span className="text-red-300">· Refugado: {bloco.refugado ?? 0}</span></p>
                            <p className="text-[#9da0ab]"><span className="text-[#5e606f]">Operador:</span> {bloco.operador || '—'}</p>
                        </>
                    )}
                    <div className="absolute w-3 h-3 bg-[#0f1219] border-b border-r border-[rgba(255,255,255,0.08)] rotate-45 -bottom-1.5 left-1/2 -translate-x-1/2"></div>
                </div>
            </div>
        );
    };

    // Bloco planejado (ZP1, 17/08/2026) — plano colocado na mão pelo PCP,
    // arrastável (move pra outro horário/máquina, mesma matemática percentual
    // do bloco real) e clicável (abre o modal de edição, diferente do modal
    // somente-leitura dos blocos reais).
    const renderPlanejadoBlock = (bloco, maquinaCodigo) => {
        const opStartMs = new Date(bloco.inicio).getTime();
        const opEndMs = new Date(bloco.fim || bloco.inicio).getTime();
        if (opEndMs <= timelineMetrics.startMs || opStartMs >= timelineMetrics.endMs) return null;

        let leftPerc = ((opStartMs - timelineMetrics.startMs) / timelineMetrics.totalMs) * 100;
        let widthPerc = ((opEndMs - Math.max(opStartMs, timelineMetrics.startMs)) / timelineMetrics.totalMs) * 100;
        // Mesmo bug real corrigido em renderOpBlock (27/08/2026) — widthPerc
        // já vem recortado à esquerda (Math.max acima), somar leftPerc de
        // novo aqui descontava a mesma parte duas vezes.
        if (leftPerc < 0) { leftPerc = 0; }
        if (leftPerc + widthPerc > 100) widthPerc = 100 - leftPerc;

        const theme = PLANEJADO_COLORS[bloco.tipo] || PLANEJADO_COLORS.PRODUCAO;
        const blockHeight = detailLevel === 'detalhado' ? 'calc(100% - 14px)' : 'calc(100% - 8px)';
        const blockTop = detailLevel === 'detalhado' ? '7px' : '4px';
        // ANDAMENTO (21/08/2026, setup rodando de verdade — ver ZP1_STATUS em
        // agendamento.js) — visual DIFERENTE de PLANEJADO: borda sólida (não
        // tracejada) + opacidade cheia sempre, porque isso já está acontecendo,
        // não é mais só um plano. Sem animação/pulse de propósito (RDP, ver
        // memória "Evitar efeitos GPU pesados" — nada de animate-pulse aqui).
        const emAndamento = bloco.status === 'ANDAMENTO';

        // Manutenção (26/08/2026) — não arrastável: diferente de Produção/
        // Setup planejados (ZP1, movidos com handleDropNaLinha via PUT
        // /api/agendamento/:id), esse item vem da ZN1 com um id sintético
        // (`mnt-<id>`, só pra servir de key/abrir o modal certo) — soltar
        // num handleDropNaLinha mandaria um PUT pra rota errada.
        const ehManutencao = bloco.origem === 'MANUTENCAO';

        return (
            <div
                key={`plan-${bloco.id}`}
                draggable={!ehManutencao}
                onDragStart={(e) => {
                    if (ehManutencao) return;
                    draggingRef.current = { id: bloco.id, maquinaOrigem: maquinaCodigo, duracaoMs: opEndMs - opStartMs };
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', bloco.id);
                }}
                onClick={() => { setSelectedOp({ ...bloco }); setFormErro(''); setIsModalOpen(true); }}
                className={`gantt-op-block ${theme.solid} text-white ${emAndamento ? 'opacity-100 border-2 border-white/70' : `opacity-80 border-2 border-dashed ${theme.border}`} flex flex-col px-3 group shadow-sm hover:shadow-md hover:opacity-100 transition-all duration-200 ${ehManutencao ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
                style={{
                    top: blockTop,
                    height: blockHeight,
                    left: `${leftPerc}%`,
                    width: `calc(${widthPerc}%)`,
                    borderRadius: '12px',
                }}
                title={`${theme.label}${emAndamento ? ' · Em andamento' : ''}${bloco.op ? ` · ${bloco.op}` : ''}`}
            >
                <div className="h-full flex flex-col justify-center pointer-events-none">
                    <div className="font-bold text-[10px] truncate leading-tight tracking-wide flex items-center gap-1">
                        {emAndamento && <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />}
                        {ehManutencao && <Wrench size={10} className="shrink-0" />}
                        {bloco.op || theme.label}
                    </div>
                    {ZOOM_LEVELS[zoomIndex].pxPorHora >= 70 && (
                        <div className="text-[9px] opacity-80 truncate leading-tight mt-0.5">
                            {emAndamento ? 'Em andamento' : (bloco.op ? `Op.${bloco.operacao} · ${hhmm(new Date(bloco.inicio))}–${hhmm(new Date(bloco.fim))}` : `${hhmm(new Date(bloco.inicio))}–${hhmm(new Date(bloco.fim))}`)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Drop de um bloco planejado (arrastado de renderPlanejadoBlock) numa nova
    // posição/máquina — calcula o novo horário a partir da posição X do drop,
    // usando a mesma matemática percentual do resto do Gantt, arredondado pro
    // quarto de hora mais próximo.
    const handleDropNaLinha = (e, maquinaDestino) => {
        e.preventDefault();
        const arrastando = draggingRef.current;
        draggingRef.current = null;
        if (!arrastando) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const percX = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        const novoInicio = snap15(new Date(timelineMetrics.startMs + percX * timelineMetrics.totalMs));
        const novoFim = new Date(novoInicio.getTime() + arrastando.duracaoMs);

        setSalvando(true);
        api.put(`/api/agendamento/${arrastando.id}`, {
            maquina: maquinaDestino,
            dtini: aaaammdd(novoInicio), hrini: hhmm(novoInicio),
            dtfim: aaaammdd(novoFim), hrfim: hhmm(novoFim),
        })
            .then(() => carregarChaoFabrica())
            .catch((err) => setErroApi(err.message || 'Não foi possível mover o agendamento.'))
            .finally(() => setSalvando(false));
    };

    // Total de itens em fila somando TODAS as máquinas — badge do botão "Fila
    // de Espera" e usado também pra montar o painel (agrupado por máquina).
    const totalFila = grupos.reduce((soma, g) => soma + g.maquinas.reduce((s, m) => s + (m.fila?.length || 0), 0), 0);
    const maquinasComFila = grupos
        .map((g) => ({ centroTrabalho: g.centroTrabalho, maquinas: g.maquinas.filter((m) => (m.fila?.length || 0) > 0) }))
        .filter((g) => g.maquinas.length > 0);
    // Lista pro seletor do modal "Reatribuir máquina" (25/08/2026) — todas as
    // máquinas já carregadas no Chão de Fábrica, sem trava por centro de
    // trabalho (decisão do usuário: qualquer máquina pode ser destino).
    const todasMaquinasCarregadas = grupos.flatMap((g) => g.maquinas.map((m) => ({ codigo: m.codigo, nome: m.nome })));

    const salvarEdicaoAgendamento = () => {
        if (!selectedOp?.id) return;
        setSalvando(true);
        setFormErro('');
        api.put(`/api/agendamento/${selectedOp.id}`, {
            tipo: selectedOp.tipo, dtini: selectedOp.dtini, hrini: selectedOp.hrini,
            dtfim: selectedOp.dtfim, hrfim: selectedOp.hrfim, observacao: selectedOp.observacao,
        })
            .then(() => { setIsModalOpen(false); return carregarChaoFabrica(); })
            .catch((err) => setFormErro(err.message || 'Não foi possível salvar o agendamento.'))
            .finally(() => setSalvando(false));
    };

    const excluirAgendamento = () => {
        if (!selectedOp?.id) return;
        setSalvando(true);
        setFormErro('');
        api.delete(`/api/agendamento/${selectedOp.id}`)
            .then(() => { setIsModalOpen(false); return carregarChaoFabrica(); })
            .catch((err) => setFormErro(err.message || 'Não foi possível excluir o agendamento.'))
            .finally(() => setSalvando(false));
    };

    const salvarNovoBloco = () => {
        if (!novoBloco) return;
        if (`${novoBloco.dtini}${novoBloco.hrini}` >= `${novoBloco.dtfim}${novoBloco.hrfim}`) {
            setFormErro('O horário final precisa ser depois do horário inicial.');
            return;
        }
        setSalvando(true);
        setFormErro('');
        api.post('/api/agendamento', novoBloco)
            .then(() => { setNovoBloco(null); return carregarChaoFabrica(); })
            .catch((err) => setFormErro(err.message || 'Não foi possível criar o agendamento.'))
            .finally(() => setSalvando(false));
    };

    const getTimelineMinWidth = () => `max(100%, ${(timelineMetrics.totalMs / 3600000) * ZOOM_LEVELS[zoomIndex].pxPorHora}px)`;

    const renderGanttTimeline = () => {
        const isNowVisible = nowMs >= timelineMetrics.startMs && nowMs <= timelineMetrics.endMs;
        const nowLeftPerc = ((nowMs - timelineMetrics.startMs) / timelineMetrics.totalMs) * 100;

        const opCounts = todosBlocos.reduce((acc, bloco) => {
            const statusVisual = statusVisualDoBloco(bloco);
            acc[statusVisual] = (acc[statusVisual] || 0) + 1;
            return acc;
        }, {});

        return (
            <div className="mes-glass rounded-3xl shadow-float-lg flex flex-col relative overflow-hidden border border-white/[0.06]">
                {/* ── Top Bar ─────────────────────────────────────────── */}
                {/* Duas linhas (18/09/2026): a barra única estourava pra direita
                    e escondia justamente a data e a navegação. Em cima, título
                    e período em destaque; embaixo, os controles de visão. */}
                <div className="flex flex-col px-6 py-4 border-b border-white/[0.04] gap-4 bg-white/[0.02] z-20">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                        <div className="bg-brand-500/10 p-3 rounded-2xl text-brand-400 shadow-sm">
                            <CalendarIcon size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-anthracite-100 text-base md:text-lg leading-tight">Mapeamento PCP</h3>
                            <p className="text-[11px] text-anthracite-500 font-medium">Controle Integrado de Ordens</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <button onClick={() => handleDateNav('prev')} title="Período anterior" className="p-2.5 rounded-xl hover:bg-white/[0.06] text-anthracite-400 hover:text-anthracite-100 transition-all duration-200"><ChevronLeft size={18} /></button>
                        <span className="periodo-gantt text-center">{formatHeaderDate()}</span>
                        <button onClick={() => handleDateNav('next')} title="Próximo período" className="p-2.5 rounded-xl hover:bg-white/[0.06] text-anthracite-400 hover:text-anthracite-100 transition-all duration-200"><ChevronRight size={18} /></button>
                        <button onClick={handleToday} className="ml-1 px-4 py-2 text-[12px] font-bold text-brand-400 hover:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 rounded-xl transition-all duration-200">Hoje</button>
                    </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {/* Detail Toggle */}
                        <div className="flex bg-surface-3/40 p-1 rounded-2xl">
                            <button
                                onClick={() => setDetailLevel('resumido')}
                                className={`flex items-center px-4 py-2 text-[11px] font-semibold rounded-xl transition-all duration-200 ${detailLevel === 'resumido' ? 'bg-brand-500/20 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300 hover:bg-white/[0.04]'}`}
                            >
                                <Layers size={13} className="mr-1.5" /> Resumido
                            </button>
                            <button
                                onClick={() => setDetailLevel('detalhado')}
                                className={`flex items-center px-4 py-2 text-[11px] font-semibold rounded-xl transition-all duration-200 ${detailLevel === 'detalhado' ? 'bg-brand-500/20 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300 hover:bg-white/[0.04]'}`}
                            >
                                <Eye size={13} className="mr-1.5" /> Detalhado
                            </button>
                        </div>

                        {/* View Mode */}
                        <div className="flex space-x-1 rounded-2xl p-1 bg-surface-3/40">
                            {['Diario', 'Semanal', 'Quinzenal', 'Mensal'].map(vm => (
                                <button key={vm} onClick={() => changeViewMode(vm)} className={`px-4 py-2 text-[11px] font-semibold rounded-xl transition-all duration-200 ${viewMode === vm ? 'bg-brand-500/20 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300 hover:bg-white/[0.04]'}`}>
                                    {vm.replace('Diario', 'Diário')}
                                </button>
                            ))}
                        </div>

                        {/* Zoom + Nav */}
                        <div className="flex items-center space-x-1 bg-surface-3/40 px-3 py-2 rounded-2xl">
                            <button onClick={() => handleZoom('out')} disabled={zoomIndex === 0} className="p-2 rounded-xl hover:bg-white/[0.06] text-anthracite-500 hover:text-anthracite-300 disabled:opacity-25 transition-all duration-200"><ZoomOut size={15} /></button>
                            {/* Granularidade atual (18/08/2026) — feedback visual de que o zoom não
                                é só "esticar", a partir de certo nível as colunas passam a ser de
                                minutos (30/15/10/5), não mais de hora cheia. */}
                            {viewMode === 'Diario' && (
                                <span className="text-[10px] font-bold text-anthracite-500 tabular-nums w-9 text-center select-none" title="Granularidade das colunas no modo Diário">
                                    {ZOOM_LEVELS[zoomIndex].minutosPorColuna === 60 ? '1h' : `${ZOOM_LEVELS[zoomIndex].minutosPorColuna}min`}
                                </span>
                            )}
                            <button onClick={() => handleZoom('in')} disabled={zoomIndex === ZOOM_LEVELS.length - 1} className="p-2 rounded-xl hover:bg-white/[0.06] text-anthracite-500 hover:text-anthracite-300 disabled:opacity-25 transition-all duration-200"><ZoomIn size={15} /></button>
                            <button onClick={toggleFullScreen} title={isFullScreen ? 'Sair da tela cheia' : 'Tela cheia'} className="ml-1 p-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-anthracite-500 hover:text-anthracite-300 transition-all duration-200">
                                {isFullScreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                            </button>
                        </div>

                        {/* Fila de Espera (18/08/2026, pedido do usuário) — antes só dava
                            pra ver a fila de UMA máquina por vez, no hover (mantido, ver
                            badge por linha abaixo); este botão abre o painel com a fila de
                            TODAS as máquinas de uma vez, visão de conjunto pro supervisor
                            decidir onde agendar o quê. */}
                        <button
                            onClick={() => setFilaPanelAberto(true)}
                            className="ml-auto flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl text-[11px] font-semibold bg-brand-500/15 text-brand-400 hover:bg-brand-500/25 transition-all duration-200"
                        >
                            <ListOrdered size={14} />
                            Fila de Espera
                            {totalFila > 0 && (
                                <span className="bg-brand-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center tabular-nums">{totalFila}</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Timeline Area ───────────────────────────────────── */}
                {/* max-h (19/08/2026, pedido do usuário: cabeçalho de horas e
                    zoom sumindo ao rolar pra baixo) — o cabeçalho de coluna
                    logo abaixo já era `sticky top-0`, mas sticky só funciona
                    de verdade dentro de um ancestral que REALMENTE tem scroll
                    interno; sem altura limitada aqui, este container nunca
                    estourava sozinho, e era a PÁGINA inteira que rolava
                    (Top Bar com zoom incluído, por estar fora deste div,
                    também subia junto). Com altura travada, só as LINHAS de
                    máquina rolam por dentro; Top Bar (acima, fora deste div)
                    e cabeçalho de horas (sticky aqui dentro) ficam fixos. */}
                <div ref={scrollContainerRef} className="relative overflow-auto custom-scrollbar-gantt scroll-smooth max-h-[calc(100vh-260px)]">
                    <div className="min-w-fit flex flex-col" style={{ minWidth: getTimelineMinWidth() }}>

                        {/* Column Header — fundo tem que ser OPACO (não
                            translúcido) pra sticky funcionar de verdade;
                            achado nesta repaginação: com opacidade, o
                            conteúdo que rola por baixo vazava através do
                            cabeçalho fixo (texto fantasma, "14:00" cortado
                            virando "4:00"). */}
                        <div className="flex border-b border-white/[0.04] sticky top-0 z-50 bg-surface-2" style={{ height: viewMode === 'Diario' ? '48px' : '60px' }}>
                            <div className="w-80 flex-shrink-0 border-r border-white/[0.04] px-4 flex items-center bg-surface-2 sticky left-0 z-50">
                                <span className="etiqueta">Setor / Máquina</span>
                            </div>
                            <div className="flex-1 flex relative">
                                {/* Cabeçalho legível (18/09/2026): hora em 12px clara; nos
                                    modos por dia, dia da semana em cima e data embaixo,
                                    com a coluna de hoje em laranja. */}
                                {/* Nos modos por dia cada coluna tem milhares de px (a
                                    escala continua sendo por hora), e o rótulo centralizado
                                    ficava fora da tela. Agora o dia gruda na esquerda ao
                                    rolar (sticky; por isso a coluna NÃO pode ter
                                    overflow-hidden) e as horas do dia vêm na linha de baixo. */}
                                {columns.map((col, index) => {
                                    if (!col.includes(', ')) {
                                        return (
                                            <div key={index} className="coluna-gantt flex-1 flex items-center justify-center overflow-hidden px-1">
                                                <span className="coluna-gantt__data truncate">{col}</span>
                                            </div>
                                        );
                                    }
                                    const [diaSemana, data] = col.split(', ');
                                    const ehHoje = col === hojeRotuloColuna;
                                    return (
                                        <div key={index} className={`coluna-gantt coluna-gantt--dia flex-1 min-w-0 flex flex-col ${ehHoje ? 'coluna-gantt--hoje' : ''}`}>
                                            <div className="flex-1 flex items-center">
                                                <span className="coluna-gantt__dia">
                                                    <span className="coluna-gantt__semana">{diaSemana}</span>
                                                    <span className="coluna-gantt__data">{data}</span>
                                                    {ehHoje && <span className="coluna-gantt__hoje">Hoje</span>}
                                                </span>
                                            </div>
                                            <div className="flex h-[22px]">
                                                {Array.from({ length: 24 }, (_, h) => (
                                                    <span key={h} className="coluna-gantt__hora flex-1">{String(h).padStart(2, '0')}h</span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex relative z-10 pb-6" style={{ height: currentY }}>

                            {/* Linhas de dependência entre operações da mesma OP (17/08/2026,
                                removidas 18/08/2026 a pedido do usuário: as setas tracejadas
                                conectando blocos de máquinas diferentes não agregavam
                                informação útil pro supervisor, só ruído visual no Gantt). */}

                            {/* Sidebar (Machine list) */}
                            <div className="w-80 flex-shrink-0 sticky left-0 z-40 border-r border-white/[0.04] flex flex-col bg-surface-2">
                                {layout.map(item => item.isHeader ? (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => toggleGroup(item.title)}
                                        title={item.collapsed ? 'Expandir grupo' : 'Recolher grupo'}
                                        className="w-full bg-white/[0.03] text-anthracite-400 font-semibold text-[10px] uppercase tracking-[0.12em] flex items-center px-4 border-b border-white/[0.04] hover:bg-white/[0.06] hover:text-anthracite-200 transition-colors duration-150"
                                        style={{ height: item.height }}
                                    >
                                        <ChevronRight size={11} className={`shrink-0 mr-1.5 text-anthracite-600 transition-transform duration-200 ${item.collapsed ? '' : 'rotate-90'}`} />
                                        <span className="flex-1 text-left truncate">{item.title}</span>
                                        <span className="text-anthracite-600 font-bold tabular-nums normal-case">{item.count}</span>
                                    </button>
                                ) : (
                                    <div key={item.id} onClick={() => setMaquinaOpsModal(item.data)} className="flex flex-col justify-center px-4 border-b border-white/[0.03] group hover:bg-brand-500/[0.04] transition-colors duration-200 cursor-pointer" style={{ height: item.height }}>
                                        <div className="flex items-center justify-between gap-1">
                                            <div className="font-semibold text-[13px] text-anthracite-200 group-hover:text-white transition-colors truncate">{item.data.codigo} - {item.data.nome}</div>
                                            {/* Agendar direto (17/08/2026, tabela ZP1) — sempre visível
                                                no hover da linha, cobre Setup/Manutenção (sem OP) e serve
                                                de fallback pra Produção sem precisar arrastar.
                                                stopPropagation (18/08/2026) — sem isso, clicar no "+"
                                                também abriria o modal "OPs da máquina" (onClick na linha
                                                inteira), quando o usuário só queria agendar direto. */}
                                            <button
                                                type="button"
                                                title="Novo agendamento nesta máquina"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const hoje = new Date();
                                                    setFormErro('');
                                                    setNovoBloco({
                                                        maquina: item.data.codigo, tipo: 'SETUP',
                                                        op: null, operacao: null, produto: null, produtoNome: null,
                                                        dtini: aaaammdd(hoje), hrini: hhmm(hoje),
                                                        dtfim: aaaammdd(hoje), hrfim: hhmm(new Date(hoje.getTime() + 3600000)),
                                                        observacao: '',
                                                    });
                                                }}
                                                className="shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded-lg text-anthracite-500 hover:text-brand-400 hover:bg-white/[0.06] transition-all duration-150"
                                            >
                                                <Plus size={13} />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                            {/* Fila (17/08/2026) — OPs que ainda vão passar por essa
                                                máquina, sem horário definido (ver contexto no plano:
                                                nunca inventamos hora pro que ainda não começou). Badge
                                                só de contagem — tooltip no hover removido (26/08/2026,
                                                pedido do usuário: já existe o modal "OPs da máquina",
                                                aberto ao clicar na linha, com a mesma informação e
                                                melhor apresentada, o hover virou redundante).
                                                Clique cai naturalmente na linha (ver onClick dela). */}
                                            {(item.data.fila || []).length > 0 ? (
                                                <span className="flex items-center gap-1 text-[9px] font-bold text-anthracite-500 tabular-nums">
                                                    <ListOrdered size={10} /> {item.data.fila.length} na fila
                                                </span>
                                            ) : <span />}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Timeline Rows */}
                            <div className="flex-1 relative">
                                {/* Grid Lines */}
                                <div className="absolute inset-0 z-0 flex pointer-events-none">
                                    {columns.map((_, i) => <div key={i} className="flex-1 border-r border-white/[0.03]"></div>)}

                                    {/* NOW Line */}
                                    {isNowVisible && (
                                        <div ref={currentTimeRef} className="absolute top-0 bottom-0 w-[2px] bg-red-500/80 z-50 rounded-full" style={{ left: `${nowLeftPerc}%` }}>
                                            <div className="absolute top-0 -left-[18px] bg-red-500 text-white text-[9px] font-bold px-2.5 py-[4px] rounded-xl shadow-lg tracking-wide">AGORA</div>
                                        </div>
                                    )}
                                </div>

                                {/* Row Lines + OP Blocks */}
                                <div className="flex flex-col absolute inset-0 z-10">
                                    {layout.map(item => item.isHeader ? (
                                        <div key={`tl-${item.id}`} className="w-full border-b border-white/[0.04] bg-white/[0.02]" style={{ height: item.height }}></div>
                                    ) : (
                                        <div
                                            key={`tl-${item.id}`}
                                            className="w-full border-b border-white/[0.03] relative hover:bg-brand-500/[0.02] transition-colors duration-200"
                                            style={{ height: item.height }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => handleDropNaLinha(e, item.data.codigo)}
                                        >
                                            {(item.data.blocos || []).map(renderOpBlock)}
                                            {(item.data.planejado || []).map((b) => renderPlanejadoBlock(b, item.data.codigo))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Legend / Status Bar ──────────────────────────────── */}
                {/* Legenda resumida (17/09/2026, pedido do usuário: "tem muitos
                    status") — a legenda antes listava TODAS as entradas de
                    STATUS_COLORS + PLANEJADO_COLORS automaticamente (Object.entries),
                    incluindo variações internas (Setup/Repreparação/Manutenção/
                    Improdutivo em andamento vs. concluído, Produzido Parcial,
                    Encerrado, Projeto Especial) que servem pra colorir os blocos
                    reais do Gantt mas deixavam a barra poluída. STATUS_COLORS e
                    PLANEJADO_COLORS continuam intactos (statusVisualDoBloco/
                    renderOpBlock ainda usam todas as chaves) — só a barra abaixo
                    ficou curada pra mostrar apenas os 5 conceitos que importam pro
                    operador: Em Execução/Pausada/Concluído (reais, bolinha cheia) e
                    Setup/Manutenção (planejados, borda tracejada).
                */}
                <div className="px-6 py-4 border-t border-white/[0.04] bg-white/[0.02] flex items-center justify-between gap-4 z-20 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        {['EM PRODUCAO', 'PARADO', 'CONCLUIDA'].map((key) => {
                            const color = STATUS_COLORS[key];
                            return (
                                <div key={key} className="gantt-status-badge">
                                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${color.bg}`}></div>
                                    <span className="text-anthracite-400 text-[12px]">{color.label}</span>
                                    {opCounts[key] !== undefined && (
                                        <span className="text-[10px] font-bold text-anthracite-500 tabular-nums">{opCounts[key]}</span>
                                    )}
                                </div>
                            );
                        })}
                        <div className="w-px h-4 bg-white/[0.06] mx-1"></div>
                        {['SETUP', 'MANUTENCAO'].map((key) => {
                            const color = PLANEJADO_COLORS[key];
                            return (
                                <div key={key} className="gantt-status-badge">
                                    <div className={`w-2.5 h-2.5 rounded-full border-2 border-dashed ${color.border}`}></div>
                                    <span className="text-anthracite-400 text-[12px]">{color.label}</span>
                                </div>
                            );
                        })}
                    </div>
                    {carregando && (
                        <span className="text-[11px] font-medium text-anthracite-500">Atualizando…</span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`flex flex-col h-full ${isFullScreen ? 'p-0 h-screen fixed inset-0 z-50 bg-surface-0' : ''}`} ref={containerRef}>
            {!isFullScreen && (
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-anthracite-50 tracking-tight flex items-center gap-2.5">
                            <Settings size={22} className="text-brand-400" /> Chão de Fábrica
                        </h1>
                        <p className="text-[14px] text-anthracite-500 mt-1">Planejamento e controle de ordens de produção</p>
                    </div>
                </div>
            )}

            <div className={`flex flex-col ${isFullScreen ? 'pt-0 h-full' : ''}`}>
                {renderGanttTimeline()}
            </div>

            {/* Modal de detalhe — Manutenção (26/08/2026, pedido do usuário:
                mostrar OS de manutenção no Gantt) — checado ANTES dos dois
                modos abaixo, senão um item PLANEJADO de manutenção (que
                precisa de `id` só pra servir de key/drag, ver
                manutencoesFuturas em producao.js) cairia no modo EDITÁVEL de
                agendamento (ZP1) por engano — PUT/DELETE em cima de um ZN1
                usando a rota de agendamento, errado. Sempre somente-leitura:
                não existe "reprogramar" uma manutenção por aqui, isso é feito
                na tela de Manutenção (abrir/concluir OS). */}
            {isModalOpen && selectedOp && selectedOp.origem === 'MANUTENCAO' && (() => {
                const emAndamento = selectedOp.status === 'MANUTENCAO_ANDAMENTO';
                const planejada = selectedOp.status === 'PLANEJADO';
                const statusLabel = emAndamento ? 'Em andamento' : planejada ? 'Planejada' : 'Concluída';
                return (
                    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                            <div className="inline-block align-bottom mes-modal rounded-3xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full animate-slide-up">
                                <div className="px-8 pt-8 pb-6">
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <div className="bg-red-500/10 p-2.5 rounded-xl mr-3">
                                                <Wrench size={18} className="text-red-400" />
                                            </div>
                                            {selectedOp.tipoOS} <span className="text-anthracite-500 font-medium text-base ml-2">· Manutenção</span>
                                        </h3>
                                        <div className="px-3 py-1.5 rounded-xl text-[11px] font-bold shrink-0 bg-red-600 text-white">
                                            {statusLabel}
                                        </div>
                                    </div>

                                    <div className="space-y-5">
                                        <div className="bg-white/[0.03] p-5 rounded-2xl border border-white/[0.04]">
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Descrição do Serviço</p>
                                            <p className="text-sm font-semibold text-white">{selectedOp.descricao || '—'}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Início</p>
                                                <p className="text-sm font-semibold text-white">{formatDate(new Date(selectedOp.inicio))}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">{planejada ? 'Previsão de Término' : 'Fim'}</p>
                                                <p className="text-sm font-semibold text-white">{emAndamento ? 'Em andamento' : (selectedOp.fim ? formatDate(new Date(selectedOp.fim)) : '—')}</p>
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Solicitante</p>
                                            <p className="text-sm font-semibold text-white">{selectedOp.solicitante || '—'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/[0.03] px-8 py-5 border-t border-white/[0.04] flex flex-row-reverse gap-3">
                                    <button type="button" className="inline-flex justify-center items-center rounded-2xl border border-white/[0.06] px-6 py-3 bg-white/[0.04] text-sm font-semibold text-anthracite-300 hover:bg-white/[0.08] hover:text-white shadow-sm transition-all duration-200" onClick={() => setIsModalOpen(false)}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal de detalhe — Improdutivo (31/08/2026, chamado MATA682) —
                mesmo tratamento somente-leitura da Manutenção acima: sem
                OP/produto, vem direto da SH6 nativa (H6_TIPO='I'), sem lugar
                pra editar por aqui (o lançamento continua sendo feito na
                rotina nativa do Protheus). */}
            {isModalOpen && selectedOp && selectedOp.origem === 'IMPRODUTIVO' && (() => {
                const emAndamento = selectedOp.status === 'IMPRODUTIVO_ANDAMENTO';
                return (
                    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                            <div className="inline-block align-bottom mes-modal rounded-3xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full animate-slide-up">
                                <div className="px-8 pt-8 pb-6">
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <div className="bg-slate-500/10 p-2.5 rounded-xl mr-3">
                                                <Pause size={18} className="text-slate-400" />
                                            </div>
                                            Improdutivo
                                        </h3>
                                        <div className="px-3 py-1.5 rounded-xl text-[11px] font-bold shrink-0 bg-slate-500 text-white">
                                            {emAndamento ? 'Em andamento' : 'Concluído'}
                                        </div>
                                    </div>

                                    <div className="space-y-5">
                                        <div className="bg-white/[0.03] p-5 rounded-2xl border border-white/[0.04]">
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Descrição</p>
                                            <p className="text-sm font-semibold text-white">{selectedOp.descricao || '—'}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Início</p>
                                                <p className="text-sm font-semibold text-white">{formatDate(new Date(selectedOp.inicio))}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Fim</p>
                                                <p className="text-sm font-semibold text-white">{emAndamento ? 'Em andamento' : (selectedOp.fim ? formatDate(new Date(selectedOp.fim)) : '—')}</p>
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Registrado por</p>
                                            <p className="text-sm font-semibold text-white">{selectedOp.operador || '—'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/[0.03] px-8 py-5 border-t border-white/[0.04] flex flex-row-reverse gap-3">
                                    <button type="button" className="inline-flex justify-center items-center rounded-2xl border border-white/[0.06] px-6 py-3 bg-white/[0.04] text-sm font-semibold text-anthracite-300 hover:bg-white/[0.08] hover:text-white shadow-sm transition-all duration-200" onClick={() => setIsModalOpen(false)}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal de detalhe — dois modos (17/08/2026): bloco REAL (ZH6,
                sem `id`) continua 100% somente-leitura, exatamente como
                antes (sem lugar real pra salvar reprogramação de trabalho já
                acontecido). Bloco PLANEJADO (ZP1, tem `id`) é editável — foi
                o PCP quem colocou ali na mão, tem onde persistir a mudança. */}
            {isModalOpen && selectedOp && selectedOp.origem !== 'MANUTENCAO' && selectedOp.origem !== 'IMPRODUTIVO' && !selectedOp.id && (() => {
                const statusVisual = statusVisualDoBloco(selectedOp);
                const theme = STATUS_COLORS[statusVisual];
                // Bloco "ao vivo" (25/08/2026) — EM PRODUCAO/SETUP_ANDAMENTO
                // não têm um fim de verdade ainda (o "fim" que a API manda é
                // só a hora da consulta, sintético, pra desenhar a barra até
                // agora — ver buscarSessoesAbertasPorRecursos/
                // buscarSetupsAbertosPorMaquinas no backend). Mostrar essa
                // hora como se fosse um horário real de término confundia —
                // "Em andamento" deixa claro que ainda está rolando.
                const emAndamento = selectedOp.status === 'EM PRODUCAO' || selectedOp.status === 'SETUP_ANDAMENTO';
                return (
                    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                            <div className="inline-block align-bottom mes-modal rounded-3xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full animate-slide-up">
                                <div className="px-8 pt-8 pb-6">
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <div className="bg-brand-500/10 p-2.5 rounded-xl mr-3">
                                                <Package size={18} className="text-brand-400" />
                                            </div>
                                            {selectedOp.op} <span className="text-anthracite-500 font-medium text-base ml-2">· Operação {selectedOp.operacao}</span>
                                        </h3>
                                        <div className={`px-3 py-1.5 rounded-xl text-[11px] font-bold shrink-0 ${theme.bg} ${theme.text}`}>
                                            {theme.label}
                                        </div>
                                    </div>

                                    <div className="space-y-5">
                                        <div className="grid grid-cols-2 gap-4 bg-white/[0.03] p-5 rounded-2xl border border-white/[0.04]">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Produto</p>
                                                <p className="text-sm font-semibold text-white">{selectedOp.produto_nome || selectedOp.produto}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Operador</p>
                                                <p className="text-sm font-semibold text-white">{selectedOp.operador || '—'}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Início</p>
                                                <p className="text-sm font-semibold text-white">{formatDate(new Date(selectedOp.inicio))}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Fim</p>
                                                <p className="text-sm font-semibold text-white">{emAndamento ? 'Em andamento' : (selectedOp.fim ? formatDate(new Date(selectedOp.fim)) : '—')}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-petrol-500/10 border border-petrol-500/15 rounded-2xl p-4">
                                                <p className="text-[10px] font-semibold text-petrol-400 uppercase tracking-wider mb-1.5">Produzido</p>
                                                <p className="text-lg font-bold text-white tabular-nums">{selectedOp.produzido ?? 0}</p>
                                            </div>
                                            <div className="bg-red-500/10 border border-red-500/15 rounded-2xl p-4">
                                                <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-1.5">Refugado</p>
                                                <p className="text-lg font-bold text-white tabular-nums">{selectedOp.refugado ?? 0}</p>
                                            </div>
                                        </div>

                                        {selectedOp.motivo && (
                                            <p className="text-[12px] text-anthracite-400"><span className="text-anthracite-500">Motivo da parada:</span> {selectedOp.motivo}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white/[0.03] px-8 py-5 border-t border-white/[0.04] flex flex-row-reverse gap-3">
                                    <button type="button" className="inline-flex justify-center items-center rounded-2xl border border-white/[0.06] px-6 py-3 bg-white/[0.04] text-sm font-semibold text-anthracite-300 hover:bg-white/[0.08] hover:text-white shadow-sm transition-all duration-200" onClick={() => setIsModalOpen(false)}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {isModalOpen && selectedOp && selectedOp.origem !== 'MANUTENCAO' && selectedOp.id && (() => {
                const theme = PLANEJADO_COLORS[selectedOp.tipo] || PLANEJADO_COLORS.PRODUCAO;
                return (
                    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                            <div className="inline-block align-bottom mes-modal rounded-3xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full animate-slide-up">
                                <div className="px-8 pt-8 pb-6">
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <div className={`${theme.tint} p-2.5 rounded-xl mr-3`}>
                                                <CalendarIcon size={18} className={theme.icon} />
                                            </div>
                                            {selectedOp.op || 'Agendamento'} {selectedOp.operacao && <span className="text-anthracite-500 font-medium text-base ml-2">· Operação {selectedOp.operacao}</span>}
                                        </h3>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {/* Status ANDAMENTO (21/08/2026) — sem isso, o modal de edição não
                                                dava nenhum sinal de que o setup já está rodando de verdade (só
                                                mostrava o tipo "Setup", igual mostraria pra um planejado ainda
                                                não iniciado). PLANEJADO/FINALIZADO não mostram badge — só
                                                ANDAMENTO precisa de destaque aqui. Sem animação (mesma regra de
                                                sempre, RDP). */}
                                            {selectedOp.status === 'ANDAMENTO' && (
                                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shrink-0 bg-white/10 border border-white/30 text-white">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />
                                                    Em andamento
                                                </div>
                                            )}
                                            <div className={`px-3 py-1.5 rounded-xl text-[11px] font-bold shrink-0 ${theme.solid} text-white`}>
                                                {theme.label}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Tipo</p>
                                            <div className="flex gap-2">
                                                {Object.entries(PLANEJADO_COLORS).map(([key, c]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => setSelectedOp((prev) => ({ ...prev, tipo: key }))}
                                                        className={`flex-1 px-3 py-2 rounded-xl text-[11px] font-bold border transition-all duration-150 ${selectedOp.tipo === key ? `${c.solid} ${c.border} text-white` : 'border-white/[0.06] text-anthracite-500 hover:text-anthracite-300'}`}
                                                    >
                                                        {c.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Data início</p>
                                                <input type="date" className="mes-input w-full" value={aaaammddParaISO(selectedOp.dtini)}
                                                    onChange={(e) => setSelectedOp((prev) => ({ ...prev, dtini: isoParaAaaammdd(e.target.value) }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Hora início</p>
                                                <input type="time" className="mes-input w-full" value={selectedOp.hrini || ''}
                                                    onChange={(e) => setSelectedOp((prev) => ({ ...prev, hrini: e.target.value }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Data fim</p>
                                                <input type="date" className="mes-input w-full" value={aaaammddParaISO(selectedOp.dtfim)}
                                                    onChange={(e) => setSelectedOp((prev) => ({ ...prev, dtfim: isoParaAaaammdd(e.target.value) }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Hora fim</p>
                                                <input type="time" className="mes-input w-full" value={selectedOp.hrfim || ''}
                                                    onChange={(e) => setSelectedOp((prev) => ({ ...prev, hrfim: e.target.value }))} />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Observação</p>
                                            <textarea className="mes-input w-full resize-none" rows={2} value={selectedOp.observacao || ''}
                                                onChange={(e) => setSelectedOp((prev) => ({ ...prev, observacao: e.target.value }))} />
                                        </div>

                                        {formErro && <p className="text-[12px] font-medium text-red-400">{formErro}</p>}
                                    </div>
                                </div>
                                <div className="bg-white/[0.03] px-8 py-5 border-t border-white/[0.04] flex items-center justify-between gap-3">
                                    <button type="button" disabled={salvando} onClick={excluirAgendamento} className="inline-flex items-center gap-1.5 rounded-2xl px-4 py-3 text-sm font-semibold text-red-400 hover:bg-red-500/10 transition-all duration-200 disabled:opacity-50">
                                        <Trash2 size={15} /> Excluir
                                    </button>
                                    <div className="flex gap-3">
                                        <button type="button" disabled={salvando} className="inline-flex justify-center items-center rounded-2xl border border-white/[0.06] px-6 py-3 bg-white/[0.04] text-sm font-semibold text-anthracite-300 hover:bg-white/[0.08] hover:text-white shadow-sm transition-all duration-200 disabled:opacity-50" onClick={() => setIsModalOpen(false)}>
                                            Cancelar
                                        </button>
                                        <button type="button" disabled={salvando} className="mes-btn-primary px-6 py-3 text-sm disabled:opacity-50" onClick={salvarEdicaoAgendamento}>
                                            Salvar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal "Fila de Espera" (18/08/2026, pedido do usuário; convertido de
                painel lateral pra modal no mesmo dia — o painel de 448px cortava
                prazo/produto, mesmo padrão de largura fixa que já causava
                truncamento em outros lugares do sistema; um modal centralizado
                mais largo dá espaço de sobra pra cada campo em sua própria coluna,
                sem cortar nada). Visão de conjunto da fila de TODAS as máquinas de
                uma vez (o tooltip por hover continua existindo pra consulta rápida
                de uma máquina só). Portal pro document.body, scrim sólido sem
                blur, mesmo `mes-modal`/`animate-slide-up` já usado no resto do
                sistema (ex.: modal "Novo agendamento" logo abaixo). Fecha
                clicando no scrim, no X, ou agendando um item. */}
            {filaPanelAberto && createPortal(
                <div
                    className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setFilaPanelAberto(false); }}
                >
                    <div className="mes-modal w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-slide-up">
                        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
                            <div className="flex items-center gap-2.5">
                                <ListOrdered size={20} className="text-brand-400" />
                                <div>
                                    <h2 className="text-[16px] font-bold text-anthracite-100">Fila de Espera</h2>
                                    <p className="text-[12px] text-anthracite-500">{totalFila} OP(s) sem horário, em {maquinasComFila.reduce((s, g) => s + g.maquinas.length, 0)} máquina(s)</p>
                                </div>
                            </div>
                            <button onClick={() => setFilaPanelAberto(false)} className="p-2 rounded-xl hover:bg-white/[0.06] text-anthracite-500 hover:text-anthracite-300 transition-colors"><X size={18} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar-thin p-6 space-y-6">
                            {maquinasComFila.length === 0 && (
                                <p className="text-[13px] text-anthracite-500 text-center py-10">Nenhuma OP na fila agora.</p>
                            )}
                            {maquinasComFila.map((g) => (
                                <div key={g.centroTrabalho}>
                                    <p className="text-[10px] font-bold text-anthracite-500 uppercase tracking-[0.12em] mb-2">{g.centroTrabalho}</p>
                                    <div className="space-y-3">
                                        {g.maquinas.map((m) => (
                                            <div key={m.codigo} className="mes-card p-4">
                                                <p className="text-[13px] font-bold text-anthracite-200 mb-3">{m.codigo} - {m.nome} <span className="text-anthracite-500 font-medium">· {m.fila.length} na fila</span></p>
                                                <div className="space-y-2">
                                                    {m.fila.map((f, i) => (
                                                        <div key={i} className={`grid ${podeReatribuir ? 'grid-cols-[170px_1fr_130px_90px_112px]' : 'grid-cols-[170px_1fr_130px_90px]'} items-center gap-3 pb-2 border-b border-white/[0.04] last:border-0 last:pb-0`}>
                                                            <div className="min-w-0">
                                                                <p className="text-[12px] font-semibold text-anthracite-100 truncate">{f.op} <span className="text-anthracite-500 font-normal">· Op.{f.operacao}</span></p>
                                                                {f.reatribuida_de && <p className="text-[10px] font-medium text-roxo-400 truncate">Reatribuída de {f.reatribuida_de}</p>}
                                                            </div>
                                                            <p className="text-[12px] text-anthracite-400 truncate">{f.produto_nome || f.produto}</p>
                                                            <p className="text-[12px] text-anthracite-500 whitespace-nowrap">Prazo: {formatPrazo(f.prazo)}</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const hoje = new Date();
                                                                    setFormErro('');
                                                                    setFilaPanelAberto(false);
                                                                    setNovoBloco({
                                                                        maquina: m.codigo, tipo: 'PRODUCAO',
                                                                        op: f.op, operacao: f.operacao, produto: f.produto, produtoNome: f.produto_nome,
                                                                        dtini: aaaammdd(hoje), hrini: hhmm(hoje),
                                                                        dtfim: aaaammdd(hoje), hrfim: hhmm(new Date(hoje.getTime() + 3600000)),
                                                                        observacao: '',
                                                                    });
                                                                }}
                                                                className="justify-self-end text-[11px] font-bold text-brand-400 hover:text-brand-300 px-2.5 py-1 rounded-lg bg-brand-500/15 hover:bg-brand-500/25 transition-colors duration-150"
                                                            >
                                                                Agendar
                                                            </button>
                                                            {podeReatribuir && (
                                                                f.reatribuida_de ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCancelarReatribuicao(f.op, f.operacao)}
                                                                        className="justify-self-end flex items-center gap-1 text-[11px] font-bold text-anthracite-400 hover:text-anthracite-200 px-2.5 py-1 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] transition-colors duration-150"
                                                                    >
                                                                        <ArrowLeftRight size={11} /> Cancelar
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setFilaPanelAberto(false);
                                                                            setReatribuirErro('');
                                                                            setReatribuirModal({ maquina: m.codigo, op: f.op, operacao: f.operacao, produtoNome: f.produto_nome || f.produto, recursoNovo: '', motivo: '' });
                                                                        }}
                                                                        className="justify-self-end flex items-center gap-1 text-[11px] font-bold text-roxo-400 hover:text-roxo-300 px-2.5 py-1 rounded-lg bg-roxo-500/15 hover:bg-roxo-500/25 transition-colors duration-150"
                                                                    >
                                                                        <ArrowLeftRight size={11} /> Reatribuir
                                                                    </button>
                                                                )
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal "OPs da máquina" (18/08/2026, pedido do usuário) — clicar na
                parte vazia da linha da máquina (ver onClick/stopPropagation acima)
                abre a fila só DESSA máquina, mesmo layout de item do painel geral
                "Fila de Espera" (grade de 4 colunas, nada trunca), sem precisar
                abrir o painel com todas as máquinas pra ver uma só. */}
            {maquinaOpsModal && createPortal(
                <div
                    className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setMaquinaOpsModal(null); }}
                >
                    <div className="mes-modal w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-slide-up">
                        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
                            <div className="flex items-center gap-2.5">
                                <ListOrdered size={20} className="text-brand-400" />
                                <div>
                                    <h2 className="text-[16px] font-bold text-anthracite-100">{maquinaOpsModal.codigo} - {maquinaOpsModal.nome}</h2>
                                    <p className="text-[12px] text-anthracite-500">{(maquinaOpsModal.fila || []).length} OP(s) na fila, sem horário</p>
                                </div>
                            </div>
                            <button onClick={() => setMaquinaOpsModal(null)} className="p-2 rounded-xl hover:bg-white/[0.06] text-anthracite-500 hover:text-anthracite-300 transition-colors"><X size={18} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar-thin p-6">
                            {(maquinaOpsModal.fila || []).length === 0 ? (
                                <p className="text-[13px] text-anthracite-500 text-center py-10">Nenhuma OP na fila agora.</p>
                            ) : (
                                <div className="mes-card p-4 space-y-2">
                                    {maquinaOpsModal.fila.map((f, i) => (
                                        <div key={i} className={`grid ${podeReatribuir ? 'grid-cols-[170px_1fr_130px_90px_112px]' : 'grid-cols-[170px_1fr_130px_90px]'} items-center gap-3 pb-2 border-b border-white/[0.04] last:border-0 last:pb-0`}>
                                            <div className="min-w-0">
                                                <p className="text-[12px] font-semibold text-anthracite-100 truncate">{f.op} <span className="text-anthracite-500 font-normal">· Op.{f.operacao}</span></p>
                                                {f.reatribuida_de && <p className="text-[10px] font-medium text-roxo-400 truncate">Reatribuída de {f.reatribuida_de}</p>}
                                            </div>
                                            <p className="text-[12px] text-anthracite-400 truncate">{f.produto_nome || f.produto}</p>
                                            <p className="text-[12px] text-anthracite-500 whitespace-nowrap">Prazo: {formatPrazo(f.prazo)}</p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const hoje = new Date();
                                                    setFormErro('');
                                                    setMaquinaOpsModal(null);
                                                    setNovoBloco({
                                                        maquina: maquinaOpsModal.codigo, tipo: 'PRODUCAO',
                                                        op: f.op, operacao: f.operacao, produto: f.produto, produtoNome: f.produto_nome,
                                                        dtini: aaaammdd(hoje), hrini: hhmm(hoje),
                                                        dtfim: aaaammdd(hoje), hrfim: hhmm(new Date(hoje.getTime() + 3600000)),
                                                        observacao: '',
                                                    });
                                                }}
                                                className="justify-self-end text-[11px] font-bold text-brand-400 hover:text-brand-300 px-2.5 py-1 rounded-lg bg-brand-500/15 hover:bg-brand-500/25 transition-colors duration-150"
                                            >
                                                Agendar
                                            </button>
                                            {podeReatribuir && (
                                                f.reatribuida_de ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCancelarReatribuicao(f.op, f.operacao)}
                                                        className="justify-self-end flex items-center gap-1 text-[11px] font-bold text-anthracite-400 hover:text-anthracite-200 px-2.5 py-1 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] transition-colors duration-150"
                                                    >
                                                        <ArrowLeftRight size={11} /> Cancelar
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setMaquinaOpsModal(null);
                                                            setReatribuirErro('');
                                                            setReatribuirModal({ maquina: maquinaOpsModal.codigo, op: f.op, operacao: f.operacao, produtoNome: f.produto_nome || f.produto, recursoNovo: '', motivo: '' });
                                                        }}
                                                        className="justify-self-end flex items-center gap-1 text-[11px] font-bold text-roxo-400 hover:text-roxo-300 px-2.5 py-1 rounded-lg bg-roxo-500/15 hover:bg-roxo-500/25 transition-colors duration-150"
                                                    >
                                                        <ArrowLeftRight size={11} /> Reatribuir
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal "Reatribuir máquina" (25/08/2026, pedido do usuário) — abre a
                partir de um item da fila (painel "Fila de Espera" ou "OPs da
                máquina"). O roteiro do Protheus continua sendo o padrão pra
                toda OP nova; isto só grava uma exceção pontual (tabela ZR1)
                pra esta operação especificamente — nunca mexe na SG2. Sem
                trava por centro de trabalho (decisão do usuário): a busca
                lista TODAS as máquinas já carregadas na tela, exceto a
                original. */}
            {reatribuirModal && createPortal(
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget && !reatribuindo) setReatribuirModal(null); }}
                >
                    <div className="mes-modal w-full max-w-md p-7 animate-slide-up">
                        <div className="flex items-center justify-between mb-1">
                            <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                                <ArrowLeftRight size={20} className="text-roxo-400" /> Reatribuir máquina
                            </h2>
                            <button onClick={() => !reatribuindo && setReatribuirModal(null)} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                        </div>
                        <p className="text-[12px] text-anthracite-500 mb-5">
                            {reatribuirModal.op} · Op.{reatribuirModal.operacao} — {reatribuirModal.produtoNome}
                        </p>
                        <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Máquina original</label>
                        <p className="text-[13px] text-anthracite-300 mb-4">{reatribuirModal.maquina}</p>

                        <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Máquina nova</label>
                        <select
                            value={reatribuirModal.recursoNovo}
                            onChange={(e) => setReatribuirModal((m) => ({ ...m, recursoNovo: e.target.value }))}
                            className="mes-input w-full mb-4"
                        >
                            <option value="">Selecione a máquina...</option>
                            {todasMaquinasCarregadas.filter((m) => m.codigo !== reatribuirModal.maquina).map((m) => (
                                <option key={m.codigo} value={m.codigo}>{m.codigo} - {m.nome}</option>
                            ))}
                        </select>

                        <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Motivo</label>
                        <textarea
                            value={reatribuirModal.motivo}
                            onChange={(e) => setReatribuirModal((m) => ({ ...m, motivo: e.target.value }))}
                            placeholder="Ex.: Manutenção corretiva na máquina original"
                            rows={2}
                            className="mes-input w-full mb-2 resize-none"
                        />
                        {reatribuirErro && <p className="text-red-400 text-[12px] mb-2">{reatribuirErro}</p>}
                        <div className="flex gap-3 mt-4">
                            <button onClick={() => setReatribuirModal(null)} disabled={reatribuindo} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3 disabled:opacity-50">Cancelar</button>
                            <button onClick={handleConfirmarReatribuicao} disabled={reatribuindo} className="flex-1 mes-btn-primary bg-roxo-600 hover:bg-roxo-500 py-3 disabled:opacity-50">
                                {reatribuindo ? 'Reatribuindo...' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de criação (17/08/2026) — aberto pelo "+" da linha da
                máquina (Setup/Manutenção/Produção em branco) ou pelo botão
                "Agendar" de um item da fila (Produção pré-preenchida). */}
            {novoBloco && (() => {
                const theme = PLANEJADO_COLORS[novoBloco.tipo] || PLANEJADO_COLORS.SETUP;
                return (
                    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setNovoBloco(null)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                            <div className="inline-block align-bottom mes-modal rounded-3xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full animate-slide-up">
                                <div className="px-8 pt-8 pb-6">
                                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/[0.06]">
                                        <h3 className="text-xl font-bold text-white flex items-center">
                                            <div className={`${theme.tint} p-2.5 rounded-xl mr-3`}>
                                                <Plus size={18} className={theme.icon} />
                                            </div>
                                            Novo agendamento
                                        </h3>
                                    </div>

                                    <div className="space-y-4">
                                        {novoBloco.op && (
                                            <div className="bg-white/[0.03] p-4 rounded-2xl border border-white/[0.04]">
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">OP / Operação</p>
                                                <p className="text-sm font-semibold text-white">{novoBloco.op} · Op.{novoBloco.operacao}</p>
                                                <p className="text-[12px] text-anthracite-400 mt-0.5">{novoBloco.produtoNome || novoBloco.produto}</p>
                                            </div>
                                        )}

                                        {!novoBloco.op && (
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Tipo</p>
                                                <div className="flex gap-2">
                                                    {['SETUP', 'MANUTENCAO'].map((key) => {
                                                        const c = PLANEJADO_COLORS[key];
                                                        return (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                onClick={() => setNovoBloco((prev) => ({ ...prev, tipo: key }))}
                                                                className={`flex-1 px-3 py-2 rounded-xl text-[11px] font-bold border transition-all duration-150 ${novoBloco.tipo === key ? `${c.solid} ${c.border} text-white` : 'border-white/[0.06] text-anthracite-500 hover:text-anthracite-300'}`}
                                                            >
                                                                {c.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Data início</p>
                                                <input type="date" className="mes-input w-full" value={aaaammddParaISO(novoBloco.dtini)}
                                                    onChange={(e) => setNovoBloco((prev) => ({ ...prev, dtini: isoParaAaaammdd(e.target.value) }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Hora início</p>
                                                <input type="time" className="mes-input w-full" value={novoBloco.hrini || ''}
                                                    onChange={(e) => setNovoBloco((prev) => ({ ...prev, hrini: e.target.value }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Data fim</p>
                                                <input type="date" className="mes-input w-full" value={aaaammddParaISO(novoBloco.dtfim)}
                                                    onChange={(e) => setNovoBloco((prev) => ({ ...prev, dtfim: isoParaAaaammdd(e.target.value) }))} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Hora fim</p>
                                                <input type="time" className="mes-input w-full" value={novoBloco.hrfim || ''}
                                                    onChange={(e) => setNovoBloco((prev) => ({ ...prev, hrfim: e.target.value }))} />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1.5">Observação</p>
                                            <textarea className="mes-input w-full resize-none" rows={2} value={novoBloco.observacao || ''}
                                                onChange={(e) => setNovoBloco((prev) => ({ ...prev, observacao: e.target.value }))} />
                                        </div>

                                        {formErro && <p className="text-[12px] font-medium text-red-400">{formErro}</p>}
                                    </div>
                                </div>
                                <div className="bg-white/[0.03] px-8 py-5 border-t border-white/[0.04] flex flex-row-reverse gap-3">
                                    <button type="button" disabled={salvando} className="mes-btn-primary px-6 py-3 text-sm disabled:opacity-50" onClick={salvarNovoBloco}>
                                        Salvar
                                    </button>
                                    <button type="button" disabled={salvando} className="inline-flex justify-center items-center rounded-2xl border border-white/[0.06] px-6 py-3 bg-white/[0.04] text-sm font-semibold text-anthracite-300 hover:bg-white/[0.08] hover:text-white shadow-sm transition-all duration-200 disabled:opacity-50" onClick={() => setNovoBloco(null)}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ChaoFabrica;
